/**
 * Rubina Irfan - Instagram Reel Dial
 *
 * Lays the reel cards out on a wide arc. The centre card is the largest and cards
 * shrink progressively toward the edges. Drag to spin the dial, click a side reel to
 * bring it forward, click the centre reel to open it on Instagram.
 *
 * With looping on, the dial spins endlessly in both directions. Cards are cloned until
 * there are enough of them that a card only ever wraps from one end of the arc to the
 * other while it is fully transparent, so the seam is never visible.
 *
 * The section renders as a plain horizontal strip until this script adds
 * `ri-ig-dial--ready`, so the reels are still usable if JS never runs.
 */
(function () {
  'use strict';

  /* Base geometry, in px at full desktop size. Everything is multiplied by the
     responsive factor `k` so the composition holds its proportions at any width. */
  var BASE = {
    cardW: 230,
    cardH: 398,
    radiusX: 1800,
    radiusY: 650,
    stageH: 620,
    dragStep: 130
  };

  var FADE_AT = 5;       // cards this far from centre are fully transparent
  var FADE_TAIL = 1.2;   // steps over which a card fades out before FADE_AT
  var OPACITY_STEP = 0.08;
  var DRAG_SLOP = 6;     // px of movement before a pointerup counts as a drag, not a click

  /* A looping dial wraps a card from +half to -half. That has to happen out beyond
     FADE_AT or you see it cross the stage, so the arc needs at least 2 * FADE_AT + 1
     cards. Fewer real reels than that are cloned up to this count. */
  var MIN_LOOP_CARDS = 2 * FADE_AT + 1;
  var MIN_LOOP_REELS = 3; // below this a loop just reads as the same image repeating

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  function scaleFor(width) {
    if (width >= 1200) return 1;
    if (width >= 900) return 0.85;
    if (width >= 700) return 0.72;
    if (width >= 500) return 0.62;
    return 0.55;
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  /* Shortest signed distance around a ring of `total` positions, in [-total/2, total/2). */
  function wrapOffset(d, total) {
    var half = total / 2;
    return mod(d + half, total) - half;
  }

  function Dial(section) {
    var stage = section.querySelector('.ri-ig-dial__stage');
    if (!stage) return;

    var cards = Array.prototype.slice.call(stage.querySelectorAll('.ri-ig-dial__card'));
    if (!cards.length) return;

    var reelCount = cards.length;
    var playing = -1;      // index of the card whose video is running
    var lastNearest = 0;
    var inView = false;    // nothing loads or plays until the dial is on screen
    var soundOn = false;   // sticky across reels once the viewer unmutes
    var angle = num(stage.dataset.angle, 8);
    var centerScale = num(stage.dataset.centerScale, 1.18);
    var falloff = num(stage.dataset.falloff, 0.13);
    var minScale = num(stage.dataset.minScale, 0.58);
    var loop = stage.dataset.loop !== 'false' && reelCount >= MIN_LOOP_REELS;
    var live = section.querySelector('.ri-ig-dial__live');

    if (loop) cloneUpToMinimum();

    var total = cards.length;
    var selected = Math.floor((reelCount - 1) / 2);
    var k = 1;
    var dragging = false;
    var dragX = 0;
    var startX = 0;
    var moved = 0;
    var suppressClick = false;

    /* Repeat the reels until the arc is long enough to hide the wrap seam. Clones are
       inert: hidden from assistive tech and stripped of theme-editor block attributes
       so the customiser still targets the one real block. */
    function cloneUpToMinimum() {
      var copies = Math.ceil(MIN_LOOP_CARDS / reelCount);
      for (var c = 1; c < copies; c++) {
        for (var j = 0; j < reelCount; j++) {
          var clone = cards[j].cloneNode(true);
          Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
            if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
          });
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          clone.dataset.riClone = 'true';
          stage.appendChild(clone);
        }
      }
      cards = Array.prototype.slice.call(stage.querySelectorAll('.ri-ig-dial__card'));
    }

    function measure() {
      k = scaleFor(section.clientWidth || window.innerWidth);
      stage.style.height = Math.round(BASE.stageH * k) + 'px';

      var w = BASE.cardW * k;
      var h = BASE.cardH * k;
      cards.forEach(function (card) {
        card.style.width = w + 'px';
        card.style.height = h + 'px';
        card.style.marginLeft = -(w / 2) + 'px';
        card.style.marginTop = -(h / 2) + 'px';
      });
    }

    function render() {
      // While dragging, `position` is fractional so the dial tracks the finger.
      var position = dragging ? selected - dragX / (BASE.dragStep * k) : selected;
      var nearest = loop
        ? mod(Math.round(position), total)
        : Math.max(0, Math.min(total - 1, Math.round(position)));

      cards.forEach(function (card, i) {
        var offset = loop ? wrapOffset(i - position, total) : i - position;
        var abs = Math.abs(offset);
        var rad = offset * angle * Math.PI / 180;

        var tx = Math.sin(rad) * BASE.radiusX * k;
        var ty = (1 - Math.cos(rad)) * BASE.radiusY * k;
        var rotate = offset * angle * 0.5;
        var scale = Math.max(minScale, centerScale - abs * falloff);

        // Ramp to zero over the last FADE_TAIL steps so cards do not pop out.
        var opacity = abs >= FADE_AT
          ? 0
          : Math.max(0, Math.min(1 - abs * OPACITY_STEP, (FADE_AT - abs) / FADE_TAIL));

        card.style.transition = (dragging || reduceMotion) ? 'none' : '';
        card.style.transform =
          'translateX(' + tx + 'px) translateY(' + ty + 'px) ' +
          'rotate(' + rotate + 'deg) scale(' + scale + ')';
        card.style.opacity = opacity;
        card.style.zIndex = String(200 - Math.round(abs * 10));

        card.classList.toggle('is-center', i === nearest);

        // Faded cards and clones must not be clickable or reachable by keyboard.
        var inert = opacity === 0 || card.dataset.riClone === 'true';
        card.style.pointerEvents = opacity === 0 ? 'none' : '';
        card.setAttribute('aria-hidden', inert ? 'true' : 'false');

        var hit = card.querySelector('.ri-ig-dial__hit');
        var sound = card.querySelector('.ri-ig-dial__sound');
        if (hit) hit.tabIndex = inert ? -1 : 0;
        if (sound) {
          // The sound toggle only enters the tab order on the reel that is playing.
          sound.tabIndex = (!inert && i === nearest && videoIn(card)) ? 0 : -1;
          // Side reels are silent, so they always show the muted icon.
          if (i !== nearest) {
            sound.classList.add('is-muted');
            sound.setAttribute('aria-pressed', 'false');
          }
        }
        if (i !== nearest) card.classList.remove('is-paused');
      });

      lastNearest = nearest;
      // Swapping the video mid-drag would thrash; settle first.
      if (!dragging) syncPlayback(nearest);
      announce(nearest);
    }

    /* ---------- Playback ---------- */

    function videoIn(card) {
      return card.querySelector('.ri-ig-dial__video');
    }

    function stop(index) {
      var card = cards[index];
      if (!card) return;
      var v = videoIn(card);
      if (!v) return;
      v.pause();
      try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
      card.classList.add('is-paused');
    }

    /* Only the front reel plays. Everything else falls back to its poster, which
       keeps us to one video decode instead of nine. */
    function syncPlayback(nearest) {
      if (playing === nearest) return;
      if (playing !== -1) stop(playing);
      playing = nearest;

      var card = cards[nearest];
      if (!card) return;
      var v = videoIn(card);
      if (!v) return;

      syncSound(card, v);

      if (reduceMotion || !inView) {
        card.classList.add('is-paused');
        return;
      }
      play(card, v);
    }

    function play(card, v) {
      if (v.preload === 'none') v.preload = 'auto';
      var attempt = v.play();
      if (attempt && attempt.then) {
        attempt.then(function () {
          card.classList.remove('is-paused');
        }).catch(function () {
          // Autoplay refused (low power mode, data saver): leave the play badge up.
          card.classList.add('is-paused');
        });
      } else {
        card.classList.remove('is-paused');
      }
    }

    function toggle(card) {
      var v = videoIn(card);
      if (!v) return;
      if (v.paused) play(card, v);
      else {
        v.pause();
        card.classList.add('is-paused');
      }
    }

    function syncSound(card, v) {
      var btn = card.querySelector('.ri-ig-dial__sound');
      v.muted = !soundOn;
      if (!btn) return;
      btn.classList.toggle('is-muted', !soundOn);
      btn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      btn.setAttribute('aria-label', soundOn ? 'Mute reel' : 'Unmute reel');
    }

    function announce(nearest) {
      if (!live) return;
      var label = 'Reel ' + (mod(nearest, reelCount) + 1) + ' of ' + reelCount;
      if (live.textContent !== label) live.textContent = label;
    }

    function select(i) {
      var next = loop ? mod(i, total) : Math.max(0, Math.min(total - 1, i));
      if (next === selected) return;
      selected = next;
      render();
    }

    /* ---------- Drag ---------- */

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      dragging = true;
      dragX = 0;
      moved = 0;
      startX = e.clientX;
      stage.classList.add('is-dragging');
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(e) {
      dragX = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dragX));
      render();
    }

    function onPointerUp() {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      var steps = Math.round(-dragX / (BASE.dragStep * k));
      suppressClick = moved > DRAG_SLOP;
      dragging = false;
      dragX = 0;
      stage.classList.remove('is-dragging');
      // Wrapping `selected` is invisible: every offset is measured around the ring.
      selected = loop
        ? mod(selected + steps, total)
        : Math.max(0, Math.min(total - 1, selected + steps));
      render();
    }

    /* ---------- Keyboard ---------- */

    function onKeyDown(e) {
      var handled = true;
      if (e.key === 'ArrowLeft') select(selected - 1);
      else if (e.key === 'ArrowRight') select(selected + 1);
      else if (e.key === 'Home') select(0);
      else if (e.key === 'End') select(reelCount - 1);
      else if (e.key === 'Enter' || e.key === ' ') {
        // Only the stage itself: on a button, let the native click through.
        if (e.target === stage) toggle(cards[loop ? mod(selected, total) : selected]);
        else handled = false;
      } else handled = false;

      if (handled) {
        e.preventDefault();
        // Keep focus on the stage: the card now at centre may be an inert clone.
        if (e.target !== stage) stage.focus();
      }
    }

    /* ---------- Wiring ---------- */

    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('keydown', onKeyDown);

    cards.forEach(function (card, i) {
      var hit = card.querySelector('.ri-ig-dial__hit');
      var sound = card.querySelector('.ri-ig-dial__sound');

      if (hit) {
        // A side reel steps the dial round; the front reel plays or pauses.
        hit.addEventListener('click', function () {
          if (suppressClick) {
            suppressClick = false;
            return;
          }
          if (i !== selected) select(i);
          else toggle(card);
        });

        // Tabbing to a reel brings it forward so focus is always on a visible card.
        hit.addEventListener('focus', function () {
          select(i);
        });
      }

      if (sound) {
        sound.addEventListener('click', function (e) {
          e.stopPropagation();
          soundOn = !soundOn;
          var v = videoIn(card);
          if (v) syncSound(card, v);
        });
      }

      card.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    });

    /* Nothing downloads or plays until the dial is actually on screen, and it stops
       again once it scrolls away. The section sits well below the fold. */
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) {
            playing = -1;
            syncPlayback(lastNearest);
          } else if (playing !== -1) {
            stop(playing);
            playing = -1;
          }
        });
      }, { threshold: 0.2 }).observe(section);
    } else {
      inView = true;
    }

    section.classList.add('ri-ig-dial--ready');
    measure();
    render();

    return {
      section: section,
      reflow: function () {
        measure();
        render();
      }
    };
  }

  /* One shared resize handler, so re-rendering the section in the theme editor
     does not pile up listeners pointing at detached nodes. */
  var instances = [];
  var resizeTimer = null;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      instances = instances.filter(function (inst) {
        return inst.section.isConnected;
      });
      instances.forEach(function (inst) {
        inst.reflow();
      });
    }, 150);
  });

  function init(root) {
    (root || document).querySelectorAll('.ri-ig-dial').forEach(function (section) {
      if (section.dataset.riDialReady === 'true') return;
      section.dataset.riDialReady = 'true';
      var instance = Dial(section);
      if (instance) instances.push(instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
